import axios from "axios";

// Points at the Cloudflare Worker. Set REACT_APP_BACKEND_URL to your Worker's
// deployed URL (or a custom domain routed to it) in the Pages build env.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

// Attach bearer token automatically when present
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("fs_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---------- Leads (Stay Informed) ----------
export async function createLead({ email, name }) {
  const { data } = await api.post("/leads", { email, name });
  return data;
}

// ---------- Assessment ----------
export async function submitAssessment({ email, answers, recommendation, package_id }) {
  const { data } = await api.post("/assessment", { email, answers, recommendation, package_id });
  return data; // { assessment_id }
}

// ---------- Checkout ----------
export async function createCheckoutSession({ package_id, assessment_id, success_url, cancel_url } = {}) {
  const origin = window.location.origin;
  const payload = {
    package_id,
    assessment_id,
    success_url: success_url || `${origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
    cancel_url: cancel_url || `${origin}/?checkout=cancelled`,
  };
  const { data } = await api.post("/checkout/session", payload);
  return data; // { url, session_id }
}

export async function getCheckoutStatus(sessionId) {
  const { data } = await api.get(`/checkout/status/${sessionId}`);
  return data; // { payment_status, package_id, customer_email }
}

export async function getAuditStatus(sessionId) {
  const { data } = await api.get(`/audit/status/${sessionId}`);
  return data; // { paid, email }
}

// ---------- Auth ----------
// NOTE: the backend derives the account email from Stripe's verified
// customer_details.email — it never trusts a client-submitted email. Only
// session_id + password are sent.
export async function signup({ session_id, password }) {
  const { data } = await api.post("/auth/signup", { session_id, password });
  return data; // { token, user }
}

export async function login({ email, password }) {
  const { data } = await api.post("/auth/login", { email, password });
  return data;
}

export async function fetchMe() {
  const { data } = await api.get("/auth/me");
  return data;
}

// ---------- Progress ----------
export async function setModuleProgress({ module_id, completed }) {
  const { data } = await api.post("/progress", { module_id, completed });
  return data;
}

// ---------- Protected course media ----------
// Course lessons are served from a private R2 bucket, gated on a paid session —
// they are NOT public CDN URLs anymore. <img>/<video> can't send an Authorization
// header directly, so we fetch as a blob and hand back an object URL; see
// hooks/useProtectedAsset.js for the caching wrapper used in Dashboard.
export async function fetchProtectedAssetBlob(key) {
  const token = localStorage.getItem("fs_token");
  const res = await fetch(`${API}/course/asset/${key}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Failed to load asset (${res.status})`);
  return res.blob();
}

// ---------- Chat ----------
export async function sendChatMessage({ session_id, message }) {
  const { data } = await api.post("/chat", { session_id, message });
  return data; // { session_id, reply }
}

// ---------- Helpers ----------
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
